'use client'

import { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'orion-theme'

/**
 * Inline, pre-paint theme resolution. Runs before first paint so there is no
 * flash of the wrong theme. Kept in one place so the layout and the provider
 * can never disagree about the storage key or the class names.
 */
export const themeInitScript = `
(function(){
  try {
    var k = '${THEME_STORAGE_KEY}';
    var stored = localStorage.getItem(k);
    var theme = stored === 'light' || stored === 'dark'
      ? stored
      : (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
    var r = document.documentElement;
    r.classList.remove('dark', 'light');
    r.classList.add(theme);
    r.style.colorScheme = theme;
  } catch (e) {
    document.documentElement.classList.add('dark');
  }
})();
`

type ThemeContextValue = {
  theme: Theme
  setTheme: (t: Theme) => void
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  setTheme: () => {},
  toggle: () => {},
})

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Dark is the SSR default (matches <html className="dark">); the effect below
  // adopts whatever the pre-paint script actually resolved.
  const [theme, setThemeState] = useState<Theme>('dark')

  useEffect(() => {
    setThemeState(document.documentElement.classList.contains('light') ? 'light' : 'dark')
  }, [])

  const setTheme = useCallback((t: Theme) => {
    const root = document.documentElement
    root.classList.remove('dark', 'light')
    root.classList.add(t)
    root.style.colorScheme = t
    try {
      localStorage.setItem(THEME_STORAGE_KEY, t)
    } catch {
      /* private mode — theme just won't persist */
    }
    setThemeState(t)
  }, [])

  const toggle = useCallback(
    () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    [theme, setTheme]
  )

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
